/**
 * Frame math for gravity reorientation (M16): a reorient zone rolls the
 * stream about its travel axis, so every station downstream of a twist
 * carries a rigid AFFINE mapping plan space (the generator's +y-up world)
 * into presented world space. Rails, autopilot and all control math stay
 * in plan space — drivers conjugate marble state in, run the unchanged
 * laws, and rotate forces back out. Pure data + functions (no three.js):
 * one truth for the client driver and the headless tests.
 */

/** Unit quaternion [x, y, z, w]. */
export type Quat = [number, number, number, number];

/** Rigid transform: world = rotate(q, plan) + t. */
export interface Affine {
  q: Quat;
  t: [number, number, number];
}

export const Q_IDENTITY: Quat = [0, 0, 0, 1];

export const AFFINE_IDENTITY: Affine = {
  q: [0, 0, 0, 1],
  t: [0, 0, 0],
};

export function quatFromAxisAngle(
  axis: readonly [number, number, number],
  angle: number,
): Quat {
  const h = angle / 2;
  const s = Math.sin(h);
  return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(h)];
}

export function quatMul(a: Quat, b: Quat): Quat {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

/** Rotate vector v by quaternion q (v' = q v q⁻¹). */
export function quatRotate(
  q: Quat,
  v: readonly [number, number, number],
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  // t = 2 q_v × v; v' = v + q_w t + q_v × t
  const tx = 2 * (q[1] * v[2] - q[2] * v[1]);
  const ty = 2 * (q[2] * v[0] - q[0] * v[2]);
  const tz = 2 * (q[0] * v[1] - q[1] * v[0]);
  out[0] = v[0] + q[3] * tx + q[1] * tz - q[2] * ty;
  out[1] = v[1] + q[3] * ty + q[2] * tx - q[0] * tz;
  out[2] = v[2] + q[3] * tz + q[0] * ty - q[1] * tx;
  return out;
}

/** Rotate v by the INVERSE of q (unit quats: conjugate). */
export function quatUnrotate(
  q: Quat,
  v: readonly [number, number, number],
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  return quatRotate([-q[0], -q[1], -q[2], q[3]], v, out);
}

export function isIdentityQuat(q: Quat): boolean {
  return q[0] === 0 && q[1] === 0 && q[2] === 0;
}

export function isIdentityAffine(a: Affine): boolean {
  return isIdentityQuat(a.q) && a.t[0] === 0 && a.t[1] === 0 && a.t[2] === 0;
}

/** world = q·plan + t. */
export function affineApply(
  a: Affine,
  p: readonly [number, number, number],
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  quatRotate(a.q, p, out);
  out[0] += a.t[0];
  out[1] += a.t[1];
  out[2] += a.t[2];
  return out;
}

/** plan = q⁻¹·(world − t). */
export function affineUnapply(
  a: Affine,
  w: readonly [number, number, number],
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  const v: [number, number, number] = [
    w[0] - a.t[0],
    w[1] - a.t[1],
    w[2] - a.t[2],
  ];
  return quatUnrotate(a.q, v, out);
}

/**
 * Compose a twist onto a running frame: rotate everything downstream by
 * `angle` about the world-space `axisWorld` line through `pivotWorld`,
 * AFTER the existing frame. The pivot is the twist point itself, so the
 * channel centerline stays continuous through the roll.
 */
export function composeTwist(
  frame: Affine,
  axisWorld: readonly [number, number, number],
  angle: number,
  pivotWorld: readonly [number, number, number],
): Affine {
  const r = quatFromAxisAngle(axisWorld, angle);
  const q = quatMul(r, frame.q);
  // world' = r·(world − pivot) + pivot; world = q0·plan + t0
  // ⇒ t' = r·(t0 − pivot) + pivot
  const rt = quatRotate(r, [
    frame.t[0] - pivotWorld[0],
    frame.t[1] - pivotWorld[1],
    frame.t[2] - pivotWorld[2],
  ]);
  return {
    q,
    t: [rt[0] + pivotWorld[0], rt[1] + pivotWorld[1], rt[2] + pivotWorld[2]],
  };
}

/** Local up (plan +y) expressed in world space. */
export function frameUp(
  a: Affine,
  out: [number, number, number] = [0, 0, 0],
): [number, number, number] {
  return quatRotate(a.q, [0, 1, 0], out);
}

/** A segment's presentation frames (EndlessSegment.frame): entry/exit
 * affines in SCALED plan coordinates, plus the in-segment twist when a
 * reorient window lives here. Segments without any reorient history
 * omit the whole record (identity fast path). */
export interface SegmentFrame {
  entry: Affine;
  exit: Affine;
  twist?: {
    /** Roll angle (radians, signed) about the travel axis. */
    angle: number;
    /** Plan-space unit travel axis of the twist run. */
    axis: [number, number, number];
    /** Plan-space centerline point where the roll begins (scaled). */
    pivot: [number, number, number];
    /** Axial length of the twist run (world units, scaled). */
    len: number;
    /** Path-window range realizing the run (inclusive). */
    from: number;
    to: number;
  };
}

/** Frame at axial parameter t (world units along the twist axis from the
 * pivot): entry before the run, exit after, a partial roll inside. */
export function segmentFrameAt(f: SegmentFrame, t: number): Affine {
  const tw = f.twist;
  if (!tw || t <= 0) return f.entry;
  if (t >= tw.len) return f.exit;
  const axisWorld = quatRotate(f.entry.q, tw.axis);
  const pivotWorld = affineApply(f.entry, tw.pivot);
  return composeTwist(f.entry, axisWorld, tw.angle * (t / tw.len), pivotWorld);
}

/** Axial parameter of a plan-space point against the twist run (world
 * units; ≤0 before, ≥len after). Segments without a twist are entry
 * everywhere — callers should shortcut on f.twist. The field is a pure
 * axial blend and therefore CONTINUOUS everywhere — the one property a
 * presentation field must keep (an attempted far-lateral snap to the
 * end frames tore a seam through geometry straddling its boundary).
 * The PLANNER guarantees no non-run geometry of the segment projects
 * axially into the open window (path.ts reorient window veto), so the
 * blend region only ever contains the run's own corridor. */
export function twistParam(
  f: SegmentFrame,
  p: readonly [number, number, number],
): number {
  const tw = f.twist;
  if (!tw) return 0;
  return (p[0] - tw.pivot[0]) * tw.axis[0] +
    (p[1] - tw.pivot[1]) * tw.axis[1] +
    (p[2] - tw.pivot[2]) * tw.axis[2];
}
